import React, { useState } from 'react';
import MediaPicker from './MediaPicker';
import { Image, X, Upload } from '@phosphor-icons/react';

/**
 * MediaPickerInput - Form input component with integrated media picker
 * 
 * Usage:
 * <MediaPickerInput
 *   label="Cover Image"
 *   value={coverImage}
 *   onChange={(url) => setCoverImage(url)}
 *   accept="image"
 * />
 */
export default function MediaPickerInput({
  label,
  value,
  onChange,
  accept = 'image',
  placeholder = 'Select or upload media',
  className = '',
  required = false,
  helpText = null,
}) {
  const [showPicker, setShowPicker] = useState(false);

  const handleSelect = (file) => {
    const url = file.storage_path;
    onChange(url);
    setShowPicker(false);
  };

  const handleClear = () => {
    onChange('');
  };

  const getPreviewUrl = () => {
    if (!value) return null;
    // If value is already a full URL, use it
    if (value.startsWith('http')) return value;
    // Otherwise, prepend backend URL
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${value}`;
  };

  const previewUrl = getPreviewUrl();

  return (
    <div className={className}>
      {label && (
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">
          {label} {required && <span className="text-red-600">*</span>}
        </label>
      )}

      <div className="space-y-2">
        {/* Preview */}
        {previewUrl && (
          <div className="relative inline-block">
            {accept === 'image' || value.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
              <img
                src={previewUrl}
                alt="Preview"
                className="max-w-full max-h-48 rounded border border-border"
              />
            ) : (
              <div className="bg-muted border border-border rounded px-4 py-8 text-center">
                <p className="text-sm text-muted-foreground">{value.split('/').pop()}</p>
              </div>
            )}
            <button
              type="button"
              onClick={handleClear}
              className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        )}

        {/* Buttons */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPicker(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90"
          >
            {value ? (
              <>
                <Image size={16} /> Change
              </>
            ) : (
              <>
                <Upload size={16} /> {placeholder}
              </>
            )}
          </button>
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted"
            >
              Remove
            </button>
          )}
        </div>

        {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
      </div>

      {/* Media Picker Modal */}
      <MediaPicker
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={handleSelect}
        accept={accept}
        multiple={false}
        title={`Select ${label || 'Media'}`}
      />
    </div>
  );
}
